import { useEffect, useState, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { apiService } from '../services/api'
import VideoCard from '../components/VideoCard'
import { ChevronLeft, ChevronRight, Search, Link, X } from 'lucide-react'

export default function HomePage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [papers, setPapers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '')
  const [filteredPapers, setFilteredPapers] = useState([])
  const [debounceTimer, setDebounceTimer] = useState(null)
  // NEU: Feedback-State für Kopier-Button
  const [copied, setCopied] = useState(false)

  const ITEMS_PER_PAGE = 9

  // Set page title
  useEffect(() => {
    document.title = 'SMW Video Archiv - Videos'
  }, [])

  // Load papers on mount
  useEffect(() => {
    const loadPapers = async () => {
      try {
        setLoading(true)
        const data = await apiService.getPapers()
        setPapers(data.data)
        setFilteredPapers(data.data)
      } catch (err) {
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }

    loadPapers()
  }, [])

  // Perform initial search from URL param
  useEffect(() => {
    if (searchQuery && papers.length > 0) {
      performSearch(searchQuery)
    }
  }, [papers])

  // Debounced search with immediate reset
  const handleSearchChange = useCallback((query) => {
    setSearchQuery(query)
    setCurrentPage(1)
    setSearchParams(query ? { search: query } : {})

    // SOFORT reset wenn leer!
    if (!query.trim()) {
      setFilteredPapers(papers)
      if (debounceTimer) clearTimeout(debounceTimer)
      return
    }

    // Sonst: debounce search
    if (debounceTimer) clearTimeout(debounceTimer)
    const timer = setTimeout(() => {
      performSearch(query)
    }, 500)
    setDebounceTimer(timer)
  }, [debounceTimer, setSearchParams, papers])

  // NEU: Link in Zwischenablage kopieren
  const handleCopyLink = useCallback(() => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }, [])

  // Search logic
  const performSearch = (query) => {
    if (!query.trim()) {
      setFilteredPapers(papers)
      return
    }

    const terms = query
      .split(',')
      .map(term => term.trim().toLowerCase())
      .filter(term => term.length > 0)

    if (terms.length === 0) {
      setFilteredPapers(papers)
      return
    }

    // Kürzel → Volltext Mapping für Suche
    const konferenztypAliases = {
      'bk': 'bundeskonferenz',
      'rk': 'regionalkonferenz',
    }

    const results = papers.filter(paper => {
      return terms.every(term => {
        const title = paper.title?.toLowerCase() || ''
        const author = paper.author1?.toLowerCase() || ''
        const kategorie = Array.isArray(paper.kategorie)
          ? paper.kategorie.map(k => k.toLowerCase()).join(' ')
          : (paper.kategorie?.toLowerCase() || '')
        const ort = paper.ort?.toLowerCase() || ''
        const bundesland = paper.bundesland?.toLowerCase() || ''
        const konferenztyp = paper.konferenztyp?.toLowerCase() || ''
        const format = paper.format?.toLowerCase() || ''

        // Kürzel auflösen: "bk" → sucht auch in "bundeskonferenz"
        const resolvedTerm = konferenztypAliases[term] || term

        if (/^\d{4}$/.test(term)) {
          return String(paper.jahr) === term
        }

        return (
          title.includes(resolvedTerm) ||
          author.includes(resolvedTerm) ||
          kategorie.includes(resolvedTerm) ||
          ort.includes(resolvedTerm) ||
          bundesland.includes(resolvedTerm) ||
          konferenztyp.includes(resolvedTerm) ||
          format.includes(resolvedTerm)
        )
      })
    })

    setFilteredPapers(results)
  }

  // Pagination
  const totalPages = Math.ceil(filteredPapers.length / ITEMS_PER_PAGE)
  const startIdx = (currentPage - 1) * ITEMS_PER_PAGE
  const endIdx = startIdx + ITEMS_PER_PAGE
  const visiblePapers = filteredPapers.slice(startIdx, endIdx)

  const handlePrev = () => {
    setCurrentPage(prev => Math.max(1, prev - 1))
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleNext = () => {
    setCurrentPage(prev => Math.min(totalPages, prev + 1))
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handlePageClick = (page) => {
    setCurrentPage(page)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const getPageNumbers = () => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1)
    }
    const pages = []
    const left = Math.max(2, currentPage - 2)
    const right = Math.min(totalPages - 1, currentPage + 2)
    pages.push(1)
    if (left > 2) pages.push('...')
    for (let i = left; i <= right; i++) pages.push(i)
    if (right < totalPages - 1) pages.push('...')
    pages.push(totalPages)
    return pages
  }

  if (loading) return <div className="text-center py-12">Lädt Videos...</div>
  if (error) return <div className="text-center py-12 text-red-600">Fehler: {error}</div>

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header mit Searchbar */}
      <div className="bg-white border-b border-gray-200 py-6 px-4 md:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-4xl font-bold mb-2">SMW Video Archiv</h1>
          <p className="text-sm text-gray-500 mb-6">
            141 Vorträge von SMW-Konferenzen 2014–2025, frei zugänglich.{' '}
            <a href="https://schule-mit-wissenschaft.de" target="_blank" rel="noopener noreferrer"
              className="underline hover:text-green-700">schule-mit-wissenschaft.de</a>
          </p>

          {/* Searchbar + Kopier-Button */}
          <div className="relative mb-4 flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-3 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Titel, Autor, Kategorie, Jahr, Ort oder Konferenztyp..."
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-600"
              />
              {searchQuery && (
                <button
                  onClick={() => handleSearchChange('')}
                  className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600 transition"
                  title="Suche löschen"
                >
                  <X size={18} />
                </button>
              )}
            </div>

            {/* NEU: Link-kopieren-Button — nur sichtbar bei aktiver Suche */}
            {searchQuery.trim() && filteredPapers.length > 0 && (
              <button
                onClick={handleCopyLink}
                title="Suchlink kopieren"
                className={
                  'flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm font-medium transition-all ' +
                  (copied
                    ? 'border-green-600 bg-green-50 text-green-700'
                    : 'border-gray-300 bg-white text-gray-600 hover:border-green-600 hover:text-green-700')
                }
              >
                <Link size={15} />
                {copied ? 'Kopiert ✓' : 'Link kopieren'}
              </button>
            )}
          </div>

          {/* Search hints */}
          <div className="text-xs text-gray-500">
            Beispiele: "Physik" • "2014" • "Erfurt" • "Higgs" • "BK" oder "Bundeskonferenz" • "Physik, 2014" (mehrere Begriffe mit Komma)
          </div>
        </div>
      </div>

      {/* Grid */}
      <div className="px-4 md:px-6 lg:px-8 py-12">
        <div className="max-w-7xl mx-auto">
          {/* Pagination oben */}
          {filteredPapers.length > 0 && totalPages > 1 && (
            <div className="mb-8">
              <div className="flex flex-col items-center gap-4">
                <div className="text-sm text-gray-500">
                  {filteredPapers.length} {searchQuery.trim() ? 'gefunden' : 'Videos'} — Seite{' '}
                  <span className="font-semibold">{currentPage}</span> von{' '}
                  <span className="font-semibold">{totalPages}</span>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => handlePageClick(1)} disabled={currentPage === 1}
                    className="px-3 py-2 rounded-lg text-sm hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition" title="Erste Seite">«</button>
                  <button onClick={handlePrev} disabled={currentPage === 1}
                    className="flex items-center gap-1 px-3 py-2 rounded-lg text-sm hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition">
                    <ChevronLeft size={16} />Vorherige</button>
                  {getPageNumbers().map((page, idx) =>
                    page === '...'
                      ? <span key={'ellipsis-top-' + idx} className="px-2 py-2 text-gray-400 text-sm select-none">…</span>
                      : <button key={page} onClick={() => handlePageClick(page)}
                        className={'px-3 py-2 rounded-lg text-sm font-medium transition ' +
                          (page === currentPage ? 'bg-green-700 text-white' : 'hover:bg-gray-100 text-gray-700')}>
                        {page}</button>
                  )}
                  <button onClick={handleNext} disabled={currentPage === totalPages}
                    className="flex items-center gap-1 px-3 py-2 rounded-lg text-sm hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition">
                    Nächste<ChevronRight size={16} /></button>
                  <button onClick={() => handlePageClick(totalPages)} disabled={currentPage === totalPages}
                    className="px-3 py-2 rounded-lg text-sm hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition" title="Letzte Seite">»</button>
                </div>
              </div>
            </div>
          )}
          {filteredPapers.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <p>Keine Videos gefunden.</p>
              <p className="text-xs mt-2">Tipp: Mehrere Begriffe mit Komma trennen, z.&nbsp;B. „Physik, 2019" oder „Berlin, BK"</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {visiblePapers.map(paper => (
                <VideoCard
                  key={paper.id}
                  paper={paper}
                  isSearchResult={searchQuery.trim().length > 0}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Pagination */}
      {filteredPapers.length > 0 && totalPages > 1 && (
        <div className="bg-white border-t border-gray-200 py-8 px-4 md:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto flex flex-col items-center gap-4">

            {/* Page count info */}
            <div className="text-sm text-gray-500">
              {filteredPapers.length} {searchQuery.trim() ? 'gefunden' : 'Videos'} — Seite{' '}
              <span className="font-semibold">{currentPage}</span> von{' '}
              <span className="font-semibold">{totalPages}</span>
            </div>

            {/* Nav row */}
            <div className="flex items-center gap-1">

              {/* Erste */}
              <button
                onClick={() => handlePageClick(1)}
                disabled={currentPage === 1}
                className="px-3 py-2 rounded-lg text-sm hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition"
                title="Erste Seite"
              >
                «
              </button>

              {/* Vorherige */}
              <button
                onClick={handlePrev}
                disabled={currentPage === 1}
                className="flex items-center gap-1 px-3 py-2 rounded-lg text-sm hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition"
              >
                <ChevronLeft size={16} />
                Vorherige
              </button>

              {/* Page numbers */}
              {getPageNumbers().map((page, idx) =>
                page === '...'
                  ? <span key={'ellipsis-' + idx} className="px-2 py-2 text-gray-400 text-sm select-none">…</span>
                  : <button
                    key={page}
                    onClick={() => handlePageClick(page)}
                    className={'px-3 py-2 rounded-lg text-sm font-medium transition ' +
                      (page === currentPage
                        ? 'bg-green-700 text-white'
                        : 'hover:bg-gray-100 text-gray-700')}
                  >
                    {page}
                  </button>
              )}

              {/* Nächste */}
              <button
                onClick={handleNext}
                disabled={currentPage === totalPages}
                className="flex items-center gap-1 px-3 py-2 rounded-lg text-sm hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition"
              >
                Nächste
                <ChevronRight size={16} />
              </button>

              {/* Letzte */}
              <button
                onClick={() => handlePageClick(totalPages)}
                disabled={currentPage === totalPages}
                className="px-3 py-2 rounded-lg text-sm hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition"
                title="Letzte Seite"
              >
                »
              </button>

            </div>
          </div>
        </div>
      )}
      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 py-6 px-4 md:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto flex flex-col items-center gap-1 text-xs text-gray-400">
          <p>SMW Video Archiv · Ein Projekt von <a href="https://schule-mit-wissenschaft.de" target="_blank" rel="noopener noreferrer" className="underline hover:text-green-700">Schule mit Wissenschaft</a></p>
          <p>
            <a href="https://schule-mit-wissenschaft.de/impressum/" target="_blank" rel="noopener noreferrer" className="underline hover:text-green-700">Impressum</a>
            {' · '}
            <a href="https://schule-mit-wissenschaft.de/datenschutz/" target="_blank" rel="noopener noreferrer" className="underline hover:text-green-700">Datenschutz</a>
          </p>
        </div>
      </footer>
    </div>
  )
}
